import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, FormControl, Select, MenuItem, Switch } from "@mui/material";
import LoadingIcon from "../../../../components/Loading";
import { icons } from "../../../../svg";
import { AddModeModalProps } from "../../../../types/IMultiLocationQuoteMobile";

const AddModeModal: React.FC<AddModeModalProps> = ({
  addModeModalOpen,
  savingRail,
  railStation,
  loadingRailStations,
  railStations,
  loadingCarriers,
  railCarriers,
  onClose,
  onRailStationChange,
  onToggleRailCarrier,
  onSaveRail,
}) => {
  const isRail = addModeModalOpen === "RAIL";
  if (!addModeModalOpen || !isRail) return null;

  const title = isRail ? "Add Inbound Rail" : "Add Inbound Truck";

  return (
    <div className="add-mode-modal-overlay">
      <div className="add-mode-modal-content">
        <div className="add-mode-header">
          <button
            type="button"
            className="icon-button-circle"
            onClick={onClose}
          >
            <ArrowBackIcon fontSize="small" />
          </button>
          <div className="add-mode-title">{title}</div>
          <div className="add-mode-header-spacer" />
        </div>

        <div className="add-mode-body">
          {isRail ? (
            <div className="add-inbound-card">
              <div className="add-rail-station-section">
                <div className="add-rail-station-row">
                  <span className="input-label-text">
                    Select Rail Station
                    <span className="required-asterisk">*</span>
                  </span>
                  <Button
                    id="verifyRailButton"
                    onClick={() => {
                      const popup = window.open(
                        "https://irfs.railinc.com/railservicefinder/#/home",
                        "RailInc Service Finder",
                        "height=1024,width=1200",
                      );
                      popup?.focus();
                    }}
                  >
                    Verify Rail Service
                    <img
                      className="verify-rail-arrow"
                      src={icons.NewSiteArrow}
                      alt="New Site Arrow"
                    />
                  </Button>
                </div>
                {loadingRailStations ? (
                  <div className="rail-loading-state">
                    <LoadingIcon height={30} width={30} color="#60A5FA" />
                  </div>
                ) : (
                  <FormControl fullWidth size="small">
                    <Select
                      value={railStation}
                      onChange={(e) => onRailStationChange(e.target.value)}
                      displayEmpty
                      disabled={
                        loadingRailStations || railStations.length === 0
                      }
                      className="custom-rail-select"
                      classes={{ root: "custom-rail-select-root" }}
                      MenuProps={{
                        disablePortal: false,
                        className: "rail-station-menu",
                        PaperProps: {
                          className: "rail-station-menu-paper",
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {loadingRailStations && "Loading rail stations..."}
                        {!loadingRailStations &&
                          railStations.length === 0 &&
                          "No rail stations linked to this zip code"}
                        {!loadingRailStations &&
                          railStations.length > 0 &&
                          "Select a rail station"}
                      </MenuItem>
                      {railStations.map((station) => (
                        <MenuItem key={station.value} value={station.value}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>

              {railStation && (
                <div
                  id="serving-carriers-section-id"
                  className="serving-carriers-section"
                >
                  <div className="input-label-text add-rail-carriers-label">
                    Serving and Connecting Carriers
                    <span className="required-asterisk">*</span>
                  </div>
                  <div className="sub-label-text add-rail-carriers-hint">
                    At least one carrier has to be toggled on. Carriers are
                    turned on by default. If a carrier is not valid, toggle it
                    off.
                  </div>

                  {loadingCarriers ? (
                    <div className="rail-loading-state">
                      <LoadingIcon height={30} width={30} color="#60A5FA" />
                    </div>
                  ) : (
                    Object.keys(railCarriers).map((carrier) => (
                      <div key={carrier} className="carrier-toggle-row">
                        <span className="carrier-name">{carrier}</span>
                        <Switch
                          id={`toggle-button-${carrier}`}
                          key={`switch-${carrier}-${
                            railCarriers[carrier as keyof typeof railCarriers]
                          }`}
                          checked={
                            railCarriers[carrier as keyof typeof railCarriers]
                          }
                          onChange={() => onToggleRailCarrier(carrier)}
                          color="primary"
                        />
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="add-inbound-card">
              <div className="sub-label-text">
                Configure inbound truck details here.
              </div>
            </div>
          )}
        </div>

        <div className="add-mode-footer">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={savingRail}
            className="add-mode-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            id="save-inbound-rail-carriers-button"
            variant="contained"
            onClick={onSaveRail}
            disabled={!railStation || savingRail}
            className={`add-mode-save-btn ${
              railStation && !savingRail ? "add-mode-save-btn-enabled" : ""
            }`}
          >
            {savingRail ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddModeModal;
